<template>
  <v-dialog v-model="dialogVisible" max-width="1000px" transition="dialog-transition">
    <v-card class="elevation-2 rounded-lg">
      <v-card-title class="text-h6 font-weight-regular py-4">
        {{ step === 1 ? $t('contract.actions.addContract') : $t('contract.actions.addcontractService') }}
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-4 pb-0">
        <v-form v-if="step === 1" ref="contractFormStep1" v-model="validStep1" @submit.prevent>
          <p v-if="client" class="body-1 mb-4">
           {{ $t('contract.actions.addContract') }}: <strong>{{ client.name }}</strong><br />
           {{ $t('onboarding.register.cnpj') }}: {{ client.cnpj }}
          </p>
          <v-select
            v-model="contractForm.contractType"
            :items="contractTypes"
            :label="$t('contract.label.contractType')"
            outlined
            dense
            :rules="[v => !!v || $t('defaultServiceValues.label.contractTypeIsRequired')]"
            class="mb-2"
          />
          <SelectDate
            v-model="contractForm.startDate"
            :data="contractForm.startDate"
            :rules="[v => !!v || $t('appointment.filters.selectDate')]"
            :label="$t('contract.label.startDate')"
            outlined
            dense
            class="mb-6"
          />
          <v-text-field
            v-model.number="contractForm.monthlyVolume"
            :label="$t('contract.label.monthlyCapacity')"
            type="number"
            outlined
            dense
            :rules="[v => v >= 0 || $t('customer.create.dailyCapacityError')]"
            class="custom-input mb-2"
          />
          <v-text-field
            v-model.number="contractForm.monthlyValue"
            :label="$t('contract.label.monthlyAmount')"
            type="number"
            outlined
            dense
            :rules="[v => v >= 0 || $t('customer.create.valueError')]"
            class="custom-input mb-2"
          />
        </v-form>

        <v-form v-if="step === 2" ref="contractFormStep2" v-model="validStep2" @submit.prevent>
          <div style="overflow-x: auto;">
            <v-data-table
              :headers="serviceHeaders"
              :items="contractForm.services"
              class="elevation-0 mb-4"
              hide-default-footer
              dense
              fixed-header
              disable-sort
            >
              <template v-slot:item.serviceType="{ item }">
                {{ getServiceTypeText(item.serviceType) }}
              </template>
              <template v-slot:item.price="{ item }">
                R$ {{ item.price }}
              </template>
              <template v-slot:item.squareMeterPrice="{ item }">
                R$ {{ item.squareMeterPrice }}
              </template>
              <template v-slot:item.baseSquareMetersSize="{ item }">
                {{ item.baseSquareMetersSize }} m²
              </template>
              <template v-slot:item.squareMeterTax="{ item }">
                {{ item.squareMeterTax }}%
              </template>
              <template v-slot:item.furnitureTax="{ item }">
                {{ item.furnitureTax }}%
              </template>
              <template v-slot:item.semiFurnitureTax="{ item }">
                {{ item.semiFurnitureTax }}%
              </template>
              <template v-slot:item.houseTax="{ item }">
                {{ item.houseTax }}%
              </template>
              <template v-slot:item.trousseauTax="{ item }">
                {{ item.trousseauTax }}%
              </template>
              <template v-slot:item.actions="{ item }">
                <div v-if="item">
                  <v-icon small class="mr-2" @click="editService(item)">mdi-pencil</v-icon>
                  <v-icon small @click="deleteService(item)">mdi-delete</v-icon>
                </div>
              </template>
            </v-data-table>
          </div>
          <v-btn color="primary" text @click="addService" class="mb-4">
            + {{ $t('contract.actions.addServiceContract') }}
          </v-btn>

          <v-dialog v-model="serviceDialog" max-width="400px">
            <v-card>
              <v-card-title class="text-h6 font-weight-regular py-4">
                {{ editingService ? $t('contract.actions.editServiceContract') : $t('contract.actions.addServiceContract') }}                <v-spacer />
                <v-btn icon @click="serviceDialog = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="pt-4 pb-0">
                <v-form ref="serviceForm" v-model="validService" @submit.prevent>
                  <v-row>
                    <v-col cols="12" sm="12" class="py-0">
                      <v-autocomplete
                        v-model="currentService.serviceType"
                        :items="serviceTypesUi"
                        item-text="name"
                        item-value="value"
                        :filter="serviceTypeFilter"
                        :label="$t('contract.label.serviceType')"
                        :no-data-text="$t('contract.noData')"
                        outlined
                        dense
                        :rules="[v => !!v || $t('defaultServiceValues.label.typeServiceIsRequired')]"
                        class="mb-2"
                        :loading="serviceTypesLoading"
                        :disabled="serviceTypesLoading"
                      />
                    </v-col>
                    <v-col cols="12" sm="12" class="py-0">
                      <v-text-field
                        v-model.number="currentService.price"
                        :label="$t('contract.label.price')"
                        outlined
                        dense
                        prefix="R$"
                        type="number"
                        :rules="[v => !!v || $t('customer.create.priceCapacityError')]"
                        class="custom-input"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0">
                      <v-text-field
                        v-model.number="currentService.squareMeterPrice"
                        :label="$t('defaultServiceValues.label.squareMetersPrice')"
                        outlined
                        dense
                        prefix="R$"
                        type="number"
                        :rules="[v => !!v || $t('customer.create.priceSquareMeterError')]"
                        class="custom-input"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0">
                      <v-text-field
                        v-model.number="currentService.baseSquareMetersSize"
                        :label="$t('contract.label.baseSquareMetersPrice')"
                        outlined
                        dense
                        suffix="m²"
                        type="number"
                        :rules="[v => v >= 0 || $t('customer.create.baseSquareMetersSizeError')]"
                        class="custom-input"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0">
                      <v-text-field
                        v-model.number="currentService.squareMeterTax"
                        :label="$t('contract.label.taxSquareMeters')"
                        outlined
                        dense
                        suffix="%"
                        type="number"
                        :rules="[v => v >= 0 && v <= 100 || $t('customer.create.taxForSquareMeters')]"
                        class="custom-input"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0">
                      <v-text-field
                        v-model.number="currentService.furnitureTax"
                        :label="$t('contract.label.furnitureTax')"
                        outlined
                        dense
                        suffix="%"
                        type="number"
                        :rules="[v => v >= 0 && v <= 100 || '']"
                        class="custom-input"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0">
                      <v-text-field
                        v-model.number="currentService.semiFurnitureTax"
                        :label="$t('contract.label.semiFurnitureTax')"
                        outlined
                        dense
                        suffix="%"
                        type="number"
                        :rules="[v => v >= 0 && v <= 100 || '']"
                        class="custom-input"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0">
                      <v-text-field
                        v-model.number="currentService.houseTax"
                        :label="$t('contract.label.houseTax')"
                        outlined
                        dense
                        suffix="%"
                        type="number"
                        :rules="[v => v >= 0 && v <= 100 || '']"
                        class="custom-input"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0">
                      <v-text-field
                        v-model.number="currentService.trousseauTax"
                        :label="$t('contract.label.trousseauTax')"
                        outlined
                        dense
                        suffix="%"
                        type="number"
                        :rules="[v => v >= 0 && v <= 100 || '']"
                        class="custom-input"
                      />
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
              <v-card-actions class="pa-4">
                <v-btn text @click="serviceDialog = false">{{ $t('realEstate.actions.cancel') }}</v-btn>
                <v-spacer />
                <v-btn
                  color="primary"
                  depressed
                  :disabled="!validService"
                  @click="saveService"
                  style="border-radius: 4px;"
                >
                  {{ $t('realEstate.actions.save') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-form>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-btn v-if="step === 2" text @click="step = 1">{{ $t('contract.actions.back') }}</v-btn>
        <v-btn v-else text @click="close">{{ $t('realEstate.actions.cancel') }}</v-btn>
        <v-spacer />
        <v-btn
          v-if="step === 1"
          color="primary"
          depressed
          :disabled="!validStep1"
          @click="nextStep"
          style="border-radius: 4px;"
        >
          {{ $t('onboarding.actions.next') }}
        </v-btn>
        <v-btn
          v-if="step === 2"
          color="primary"
          depressed
          :disabled="!validStep2 || !contractForm.services.length"
          @click="submit"
          style="border-radius: 4px;"
        >
          {{ $t('contract.actions.createContract') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SelectDate from '@/components/date/SelectDate.vue'
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import { normalizeTextSearch } from '@/util/helpers'

export default {
  name: 'CreateContractDialog',

  components: {
    SelectDate,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    client: {
      type: Object,
      default: null,
    },
  },

  // TODO: Add translation for headers and types
  data() {
    return {
      step: 1,
      validStep1: false,
      validStep2: false,
      validService: false,
      serviceDialog: false,
      editingService: null,

      serviceHeaders: [
        { text: 'Tipo', value: 'serviceType', width: '150px', align: 'center', sortable: false },
        { text: 'Preço', value: 'price', width: '100px', align: 'center', sortable: false },
        { text: 'Preço por m²', value: 'squareMeterPrice', width: '120px', align: 'center', sortable: false },
        { text: 'Tamanho base m²', value: 'baseSquareMetersSize', width: '120px', align: 'center', sortable: false },
        { text: 'Taxa por m²', value: 'squareMeterTax', width: '100px', align: 'center', sortable: false },
        { text: 'Taxa de mobília', value: 'furnitureTax', width: '120px', align: 'center', sortable: false },
        { text: 'Taxa de semimobiliado', value: 'semiFurnitureTax', width: '120px', align: 'center', sortable: false },
        { text: 'Taxa de casa', value: 'houseTax', width: '120px', align: 'center', sortable: false },
        { text: 'Taxa de enxoval', value: 'trousseauTax', width: '120px', align: 'center', sortable: false },
        { text: 'Ações', value: 'actions', sortable: false, width: '100px', align: 'center' },
      ],

      contractForm: {
        contractType: '',
        startDate: '',
        monthlyVolume: 0,
        monthlyValue: 0,
        services: [],
      },

      currentService: {
        serviceType: '',
        price: 0,
        squareMeterPrice: 0,
        baseSquareMetersSize: 0,
        squareMeterTax: 0,
        furnitureTax: 0,
        semiFurnitureTax: 0,
        houseTax: 0,
        trousseauTax: 0,
      },
    }
  },

  computed: {
    ...mapGetters([
      'serviceProviderContractServiceTypes',
      'serviceProviderContractServiceTypesLoading',
    ]),

    dialogVisible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },

    contractTypes() {
      return [
        {
          text: this.$t('contract.label.subscriptionTypes.POSTPAID'),
          value: 'POSTPAID',
        },
        {
           text: this.$t('contract.label.subscriptionTypes.PREPAID'),
            value: 'PREPAID',
        },
      ]
    },

    serviceTypesLoading() {
      return this.serviceProviderContractServiceTypesLoading
    },

    serviceTypesUi() {
      return (this.serviceProviderContractServiceTypes || [])
        .filter(x => x && x.value)
        .map(x => {
          const key = `type.${x.value}`
          const translated = this.$t(key)
          return {
            value: x.value,
            name: (translated && translated !== key) ? translated : (x.name || x.value),
          }
        })
    },
  },

  watch: {
    async dialogVisible(visible) {
      if (visible) {
        this.resetForm()
        await this.fetchServiceProviderContractServiceTypes()
      } else {
        this.$refs.contractFormStep1?.resetValidation()
        this.$refs.contractFormStep2?.resetValidation()
        this.$refs.serviceForm?.resetValidation()
        this.step = 1
        this.serviceDialog = false
      }
    },
  },

  methods: {
    ...mapActions([
      'fetchServiceProviderContractServiceTypes',
      'createServiceProviderContract',
    ]),

    serviceTypeFilter(item, queryText, itemText) {
      const query = normalizeTextSearch(queryText).trim()
      if (!query) return true

      const target = normalizeTextSearch(itemText || item?.name || item?.value)
      return target.includes(query)
    },

    getServiceTypeText(value) {
      if (!value) return ''

      const key = `type.${value}`
      const translated = this.$t(key)
      if (translated && translated !== key) return translated

      const found = this.serviceTypesUi.find(t => t.value === value)
      return found?.name ?? value
    },

    resetForm() {
      this.contractForm = {
        contractType: '',
        startDate: '',
        monthlyVolume: 0,
        monthlyValue: 0,
        services: [],
      };
      this.currentService = {
        serviceType: '',
        price: 0,
        squareMeterPrice: 0,
        baseSquareMetersSize: 0,
        squareMeterTax: 0,
        furnitureTax: 0,
        semiFurnitureTax: 0,
        houseTax: 0,
        trousseauTax: 0,
      };
      this.editingService = null;
    },

    close() {
      this.dialogVisible = false;
      this.resetForm();
    },

    nextStep() {
      if (this.$refs.contractFormStep1.validate()) {
        this.step = 2;
      }
    },

    addService() {
      this.editingService = null;
      this.currentService = {
        serviceType: '',
        price: 0,
        squareMeterPrice: 0,
        baseSquareMetersSize: 0,
        squareMeterTax: 0,
        furnitureTax: 0,
        semiFurnitureTax: 0,
        houseTax: 0,
        trousseauTax: 0,
      };
      this.serviceDialog = true;
    },

    editService(service) {
      this.editingService = service;
      this.currentService = { ...service };
      this.serviceDialog = true;
    },

    deleteService(service) {
      this.contractForm.services = this.contractForm.services.filter(s => s !== service);
      this.validStep2 = this.$refs.contractFormStep2.validate();
    },

    saveService() {
      if (this.$refs.serviceForm.validate()) {
        if (this.editingService) {
          Object.assign(this.editingService, this.currentService);
        } else {
          this.contractForm.services.push({ ...this.currentService });
        }
        this.serviceDialog = false;
        this.validStep2 = this.$refs.contractFormStep2.validate();
      }
    },

    async submit() {
      if (!this.$refs.contractFormStep2.validate() || !this.contractForm.services.length) return

        try {
        if (!this.contractForm.contractType) {
          console.error('subscription_type is required')
          return
        }

          await this.createServiceProviderContract({
          real_estate_id: this.client.id,
          subscription_type: this.contractForm.contractType,
          start_date: this.contractForm.startDate ? moment(this.contractForm.startDate).format('YYYY-MM-DD') : null,
          end_date: null,
          monthly_capacity: this.contractForm.monthlyVolume,
          daily_capacity: this.contractForm.monthlyValue,
          service_contracts: this.contractForm.services.map(s => ({
          type: s.serviceType,
          price: s.price.toString(),
          square_meter_price: s.squareMeterPrice.toString(),
          base_square_meters_size: s.baseSquareMetersSize,
          square_meter_tax: s.squareMeterTax.toString(),
          furniture_tax: s.furnitureTax.toString(),
          semi_furniture_tax: (s.semiFurnitureTax ?? 0).toString(),
          house_tax: (s.houseTax ?? 0).toString(),
          trousseau_tax: s.trousseauTax.toString(),
          discount: "0",
          host_tax: "0",
          displacement_tax: "0",
          host_realty_feature_tax: "0",
          urgency_tax: "0",
          host_urgency_tax: "0",
          urgency_hours_limit: 0
          })),
        });

    this.$emit('submit', this.contractForm);
      this.close();
        } catch (error) {
          console.error('Erro ao criar contrato:', error);
      }
    },
  }
};
</script>
